<template lang="pug">
dialog(ref='dialog' @keydown.esc.prevent="emit('close')" :class="modalClass")
    slot
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed, onUnmounted, nextTick } from "vue";
import { setModalOpen } from "@/components/navBar-autohide";

// ** 기본 모달 제외 (스크롤 가능한 모달 경우) '.modal-scroll' 클래스명 추가하여 사용 (ex. Modal.modal-scroll)

let props = defineProps({
    open: Boolean,
    variant: {
        type: String,
        default: "default",
    },
});

const emit = defineEmits(["close"]);
// dialog 엘리먼트 참조
const dialog = ref<HTMLDialogElement | null>(null);

const modalClass = computed(() => {
    if (props.variant === "modal") {
        return "modal";
    }
    return ""; // 기본 스타일
});

// iOS Safari viewport height 계산
const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
};

/**
 * 스크롤 가능한 컨테이너들을 전부 맨 위로 초기화
 * dialog 자체 + 내부 스크롤 영역(.middle, .modal-body 등)
 */
const resetScroll = () => {
    if (!dialog.value) return;

    // dialog 자체
    dialog.value.scrollTop = 0;
    dialog.value.scrollTo?.(0, 0);
};

watch(
    () => props.open,
    async (nv) => {
        setModalOpen(nv);

        if (nv) {
            // 모달 표시
            dialog.value?.showModal();

            // nextTick 이후 레이아웃 확정 -> 스크롤 초기화
            await nextTick();
            resetScroll();
            // 혹시나 브라우저 렌더 타이밍 이슈 대비 1 frame 뒤 한 번 더
            requestAnimationFrame(resetScroll);

            document.body.style.overflow = "hidden";
            document.body.style.position = "fixed";
        } else {
            dialog.value?.close();
            document.body.style.overflow = "";
            document.body.style.position = "";
        }
    }
);

window.addEventListener("focusin", () => {
    // 키보드 올라올 때
    document.body.style.height = "100dvh";
    setViewportHeight();
});

window.addEventListener("focusout", () => {
    // 키보드 내려갈 때
    document.body.style.height = "";
    setViewportHeight();
});

window.addEventListener("resize", setViewportHeight);

onMounted(async () => {
    setViewportHeight();

    if (props.open) {
        dialog.value?.showModal();
        await nextTick();
        resetScroll();
        requestAnimationFrame(resetScroll);
    }
});

onUnmounted(() => {
    document.body.style.overflow = "";
    window.removeEventListener("resize", setViewportHeight);
});
</script>

<style lang="less">
dialog,
.dialog {
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    outline: none;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
    background-color: rgba(22, 23, 26, 1);
    color: #fff;
    text-align: center;
    padding: 4rem;

    overscroll-behavior: contain;
    touch-action: manipulation;
    -webkit-overflow-scrolling: touch;

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.9);
    }

    .modal-prev,
    .modal-close {
        position: absolute;
        top: 1.5rem;
        width: 2rem;
        height: 2rem;
        background-color: transparent;
        border: none;
        cursor: pointer;
        opacity: 0.8;

        &:hover {
            opacity: 1;
        }
    }

    .modal-prev {
        left: 1.5rem;
    }

    .modal-close {
        right: 1.5rem;
        border-radius: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;

        &:hover {
            background-color: rgba(255, 255, 255, 0.05);
        }
    }

    .modal-title {
        font-size: 1.75rem;
        font-weight: 500;
        margin: 0 0 1rem;

        &.caution {
            color: var(--caution-color);
        }
    }

    .modal-desc {
        // max-width: 360px;
        margin: 0 auto;
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.4;
        color: rgba(102, 102, 102, 1);
        margin-bottom: 2rem;
    }

    .modal-btns {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-top: 0.75rem;

        .loader-wrap {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 45px;
            text-align: center;
        }

        &.end {
            justify-content: flex-end;
        }
    }

    @media (max-width: 430px) {
        .modal-btns {
            flex-direction: column-reverse;
            align-items: center;
            gap: 0.5rem;

            button {
                width: 100%;
            }
        }
    }
}

// 모달 스타일 추가 (스크롤O) :: s
dialog {
    * {

        // 스크롤 처리
        &::-webkit-scrollbar {
            width: 0.25rem;
            height: 0.25rem;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.5);
            border-radius: 12px 12px 12px 12px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }
    }

    // 스크롤 처리
    &::-webkit-scrollbar {
        width: 0.25rem;
        height: 0.25rem;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.5);
        border-radius: 12px 12px 12px 12px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    label {
        &.flex {
            display: flex;
            flex-direction: column;
        }

        span.label {
            width: fit-content;
        }
    }

    textarea {
        width: 100%;
        height: 160px;
        resize: none;
        tab-size: 2;
        font-family: monospace;
        white-space: pre;
        border-radius: 0.5rem;
        background: linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), #16171a;
        border: 0;
        color: #fff;
        padding: 1rem 1.25rem;

        &:focus-visible {
            outline: 1px solid #0a4df1;
        }
    }
}

// 이전 스타일
.modal-scroll {
    background-color: #16171a;
    padding: 0 2rem;
    text-align: left;
    width: calc(100% - 0.5rem);
    max-height: calc(100% - 0.5rem);
    max-width: 50rem;

    button {
        padding: 0.875rem 1rem;
        max-width: 6.25rem;
        margin-left: auto;
    }

    .modal-header {
        padding: 1.5rem 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        border-bottom: 1px solid rgba(225, 225, 225, 0.1);

        position: sticky;
        top: 0;
        max-width: 45.625rem;
        width: 100%;
        background-color: #16171a;
        z-index: 9;
    }

    .title {
        font-size: 2rem;
        font-weight: 500;
        line-height: 1.3;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
    }

    .btn-close {
        background-color: transparent;
        padding: 0.5rem;
        max-width: fit-content;
    }

    .modal-body {
        padding: 2rem 0;
    }

    .content {
        padding: 0;
        font-size: 1rem;
    }

    .modal-footer {
        padding: 2rem 0;
        border-top: 1px solid rgba(225, 225, 225, 0.1);
        text-align: right;
    }

    // 기존 구조에 맞춰서 스타일링
    .modal-title {
        padding: 1.5rem 0;
        border-bottom: 1px solid rgba(225, 225, 225, 0.1);
        margin-bottom: 0;
    }
}

.modal-form {
    max-width: 560px;
    width: calc(100% - 1rem);
    background-color: unset;
    padding: 0;
    border-radius: 0;

    .modal-close {
        z-index: 9999;
        position: relative;
        left: initial;
        bottom: initial;
        top: 0;
        right: 0;
        margin-bottom: 0.5rem;
        margin-left: auto;

        .btn-close {
            padding: 0;
            background: transparent;
            height: 100%;
            min-height: 100%;
        }
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        background-color: rgba(22, 23, 26, 1);
        padding: 1rem;
        // margin-bottom: 1rem;
        // border-radius: 7px;
    }

    .modal-header {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        justify-content: space-between;
        gap: 10px;

        padding: 1.25rem 1rem;
        border-radius: 7px 7px 0 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        .title {
            font-size: 1.3rem;
        }

        .desc {
            text-align: left;
            font-size: 0.875rem;
            color: #666;
            // margin-top: 0.25rem;
            width: 100%;
        }
    }

    .modal-body {
        max-height: calc(100vh - 24rem);
        overflow-y: auto;
        text-align: left;

        &.rounded {
            border-radius: 0 0 7px 7px;
            margin-bottom: 1rem;
        }

        &.no-max-height {
            max-height: unset;
        }

        label:not(._checkbox) input,
        label textarea {
            margin-top: 8px;
        }

        .label {
            padding-left: 4px;
            font-weight: 300;
        }

        .error {
            margin-top: 1rem;
            margin-bottom: 0;
        }
    }

    .modal-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;

        margin-bottom: 1rem;
        border-radius: 0 0 7px 7px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);

        .button-wrap {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        button {
            padding: 0.8rem;
            height: unset;
            min-height: unset;
        }
    }

    // 모달 상세 (보기 부분) 스타일 추가 :: s
    .view-part {
        background-color: rgba(225, 225, 225, 0.05);
        padding: 1.5rem;
        border-radius: 0.5rem;
        margin-bottom: 2rem;

        .row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            font-size: 0.9rem;
            margin-bottom: 1.2rem;
            margin-top: 0;
            gap: 0.5rem;

            &:last-of-type {
                margin-bottom: 0;
            }

            &.line {
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                padding-bottom: 2rem;
                margin-bottom: 2rem;
            }

            &.indent {
                padding-left: 20px;

                .key {
                    font-weight: normal;
                    width: 150px;
                }
            }

            .key {
                position: relative;
                font-weight: 300;
                width: 170px;
            }

            .value {
                color: #aaa;
                font-weight: 300;
                min-width: fit-content;
                width: fit-content;
                margin: 0;
                flex: 1;
                word-break: break-all;

                input {
                    width: 100%;
                }
            }

            .verified {
                font-size: 0.8rem;
                font-weight: 300;
            }
        }
    }

    // 모달 상세 (편집 부분) 스타일 추가 :: s
    .edit-part {
        .row {
            margin-bottom: 1.2rem;

            &:last-child {
                margin-bottom: 0;
            }

            &.line {
                border-bottom: 1px solid rgba(225, 225, 225, 0.1);
                padding-bottom: 1.5rem;
                margin-bottom: 1rem;
            }

            &.flex {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 1rem;

                .key {
                    margin: 0;
                }
            }

            .key {
                margin-bottom: 0.5rem;
                font-weight: 300;
                font-size: 0.9rem;

                &.flex {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
            }

            .value {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.625rem;
                flex-grow: 1;
                word-break: break-all;

                input,
                select {
                    flex-grow: 1;
                }

                .fix {
                    flex-grow: unset;
                    width: 180px;
                }

                // &.file-wrap {
                //     flex-direction: column;
                //     align-items: flex-start;
                //     gap: 0.5rem;
                // }

                &.block {
                    display: block;
                }
            }
        }
    }
}

.modal-search {
    max-width: 560px;
    width: calc(100% - 1rem);
    background-color: unset;
    padding: 0;
    border-radius: 0;

    .modal-close {
        z-index: 9999;
        position: relative;
        left: initial;
        bottom: initial;
        top: 0;
        right: 0;
        margin-bottom: 0.5rem;
        margin-left: auto;

        .btn-close {
            padding: 0;
            background: transparent;
            height: 100%;
            min-height: 100%;
        }
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        background-color: rgba(22, 23, 26, 1);
        padding: 1rem;
        margin-bottom: 1rem;
        border-radius: 7px;
    }

    .modal-header {
        position: relative;
        padding: 8px;

        .search-for {
            position: absolute;
            left: 8px;
            top: 50%;
            transform: translateY(-50%);
            color: #666;
            z-index: 2;

            svg {
                fill: #666;
                margin: 0 8px;
            }
        }

        .search-input {
            position: relative;
            width: 100%;

            &.readonly {
                cursor: pointer;

                input {
                    cursor: pointer;
                }
            }

            input {
                background: unset;
            }

            svg {
                position: absolute;
                right: 8px;
                top: 50%;
                transform: translateY(-50%);
                fill: #666;
                cursor: pointer;
            }
        }
    }

    .modal-body {
        max-height: calc(100vh - 10rem);

        border-radius: 7px 7px 0 0;
        margin-bottom: 0;
        padding: 0;

        .search-options {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 1rem;

            .center-text {
                width: 100%;
                color: #666;
                text-align: center;
                padding-left: 8px;
                font-size: 0.9rem;
            }

            .btn-option {
                padding: 0.8rem;
                height: unset;
                min-height: unset;
            }
        }

        .advanced-search {
            max-height: calc(100vh - 580px);
            overflow-y: auto;
            border-top: 1px solid rgba(255, 255, 255, 0.10);
            padding: 1rem;

            @media (max-width: 768px) {
                max-height: calc(100vh - 460px);
            }

            @media (max-width: 490px) {
                max-height: calc(100vh - 510px);
            }

            // @media (max-width: 340px) {
            //     max-height: unset;
            // }
        }

        .bottom-btns {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: end;
            gap: 1rem;
            padding-top: 1rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);

            button {
                height: unset;
                min-height: unset;
            }
        }
    }

    .modal-footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        border-radius: 0 0 7px 7px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);

        .button-wrap {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        button {
            padding: 0.8rem;
            height: unset;
            min-height: unset;
        }
    }
}

@media (max-width: 768px) {
    .modal-form {
        .view-part {
            .row {
                .value {
                    width: 100%;
                    flex: unset;
                }
            }
        }
    }
}

@media (max-width: 430px) {

    dialog,
    .dialog {
        max-width: calc(100% - 1rem);
        max-height: calc(100% - 1rem);
        width: calc(100% - 1rem);
        height: fit-content;
        // border-radius: 0;
        padding: 4rem 1.5rem;
    }

    .modal-scroll {
        padding: 0 1rem;
    }

    .modal-form,
    .modal-search {
        max-height: 100%;
        width: 100%;

        .modal-body {
            max-height: calc(100vh - 15rem);
        }
    }

    .modal-form {
        .view-part {
            .row {
                gap: 0.5rem 1.25rem;
            }
        }

        .edit-part {
            .row {
                .value {
                    flex-direction: column;
                    align-items: flex-start;

                    input,
                    select,
                    .fix {
                        width: 100% !important;
                    }
                }
            }
        }
    }

    // 모바일 전용 스타일 추가 :: s
    .modal-form.bigscreen,
    .modal-search.bigscreen {
        width: 100%;
        height: 100%;
        max-height: 100%;

        .modal-close {
            display: none;
        }

        .modal-header,
        .modal-body,
        .modal-footer {
            border-radius: 0;
        }

        .modal-body {
            max-height: calc(100dvh - 143px);
            max-height: calc(var(--vh, 1vh) * 100 - 143px);

            @supports (-webkit-touch-callout: none) {
                max-height: -webkit-fill-available;
                max-height: calc(100dvh - 143px);
            }
        }

        .modal-footer {
            margin: 0;
        }
    }

    .modal-search.bigscreen {
        .modal-body {
            min-height: calc(100dvh - 157px);
        }

        &.search-users {
            .advanced-search {
                max-height: calc(100dvh - 368px);
            }
        }

        &.search-database {
            .advanced-search {
                max-height: calc(100dvh - 261px);
            }
        }

    }
}
</style>
